import { ReactNode } from 'react'

import {
  differentAllowanceColumns,
  sameAllowanceColumns,
} from 'adminDashboard/benefitDetails/common/AgeAllowanceColumns'
import { Col, Row, Skeleton, Table } from 'antd'

import { ProductIds } from 'core'
import { ClassType } from 'core/application/adminDashboard/benefitDetails/design/productClass/selectClass'
import OrderedPageFilter from 'core/application/commons/orderedPageFilter'
import { PagedProductEligibleEmployee } from 'core/application/csiDashboard/csiSearch/getProductEligibleEmployees'
import AllowanceType from 'core/domain/benefit/allowanceType'
import { ProductClass } from 'core/domain/benefit/productClass'
import { FamilyStatus } from 'core/domain/companies/familyStatus'

import { parseZoneFormat } from 'common/dateFormatters'
import { formatMoney } from 'common/moneyFormatter'

import {
  getAllowanceByFamilyStatus,
  getCostSharingPercentageAmountByFamilyStatus,
  getDeductiblesAmountByFamilyStatus,
} from '../../../adminDashboard/benefitDetails/common/ProductClassHelpers'
import ExpandableTable from '../dataDisplay/ExpandableTable'
import ClassSummary from './ClassSummary'

import './ClassesSummary.less'

interface ClassesSummaryProps {
  classes: { name: string; id: string; totalEmployeesAssigned?: number }[]
  sameClassForAll: boolean
  apiEndpoint: (id: string) => Promise<ProductClass>
  eligibleEmployeesEndpoint?: (filter: OrderedPageFilter, id: string) => Promise<PagedProductEligibleEmployee>
  productId: ProductIds
}

const getClassSummary = (
  item: ProductClass | null,
  employees: PagedProductEligibleEmployee | null,
  translate: (key: string) => string,
  setEmployeesFilter: (values: OrderedPageFilter) => void,
  productId: ProductIds,
): ReactNode => {
  const isQsehraorIchra = productId === ProductIds.QsehraId || productId === ProductIds.IchraId

  return (
    <Skeleton active loading={item === null}>
      {item !== null ? (
        <Row>
          {getClassTypeContent(item, translate)}
          {getAllowanceContent(item, translate)}
          {!isQsehraorIchra && getProrateAllowanceContent(item, translate)}
          {!isQsehraorIchra && getEobContent(item, translate)}
          {!isQsehraorIchra && getCostSharingAmountContent(item, translate)}
          {!isQsehraorIchra && getCostSharingPercentage(item, translate)}
          {getWaitingPeriodContent(item, translate)}
          {productId === ProductIds.QsehraId && getReimburseAnotherEmployeePlan(item, translate)}
          {isQsehraorIchra && getExpenseType(item, translate)}
          {employees !== null && getEmployees(employees, translate, setEmployeesFilter)}
        </Row>
      ) : null}
    </Skeleton>
  )
}
const getProrateAllowanceContent = (item: ProductClass, translate: (key: string) => string): ReactNode => {
  return (
    <Col span={12} className="class_section pl-8 pr-8">
      <div className="title">
        <h6>{translate('admin-dashboard.benefit-details.summary.labels.prorate-allowances')}</h6>
      </div>
      <div className="content">
        <p>
          {translate('admin-dashboard.benefit-details.summary.labels.upon-hire')}:{' '}
          {item.prorateAllowancesHire !== null && item.prorateAllowancesHire.toString()}
        </p>
        <p>
          {translate('admin-dashboard.benefit-details.summary.labels.upon-termination')}:{' '}
          {item.prorateAllowancesTerminate !== null && item.prorateAllowancesTerminate.toString()}
        </p>
      </div>
    </Col>
  )
}
const getClassTypeContent = (item: ProductClass, translate: (key: string) => string): ReactNode => {
  return item.classType?.id !== ClassType.AllEligibleEmployees ? (
    <Col span={12} className="class_section pl-8 pr-8">
      <div className="title border_bottom">
        <h6>{translate('admin-dashboard.benefit-details.summary.class-employee')}</h6>
      </div>
      <div className="content">{item.name}</div>
    </Col>
  ) : null
}

const getAllowanceContent = (item: ProductClass, translate: (key: string) => string): ReactNode => {
  const showAgeAllowanceTable = item.familyAgeAllowances.length > 0

  return (
    <Col span={12} className="class_section pl-8 pr-8">
      <div className="title border_bottom">
        <h6>
          {translate('admin-dashboard.benefit-details.summary.class-employee')}
          {item.offerSameAllowance === null ? ': Not set' : ''}
        </h6>
      </div>
      {!showAgeAllowanceTable && item.offerSameAllowance !== null && (
        <div className="content">
          {item.offerSameAllowance
            ? sameAllowanceContent(item, translate)
            : differentAllowanceContentByFamilyStatus(item, translate)}
        </div>
      )}
      {showAgeAllowanceTable && item.offerSameAllowance !== null && (
        <div className="content">
          <ExpandableTable
            className={'t-product-class-details w-100'}
            dataSource={item.familyAgeAllowances}
            loading={false}
            columns={
              item.offerSameAllowance
                ? sameAllowanceColumns(item.familyAgeAllowances)
                : differentAllowanceColumns(item.familyAgeAllowances)
            }
            pagination={false}
          />
        </div>
      )}
    </Col>
  )
}

const sameAllowanceContent = (item: ProductClass, translate: (key: string) => string): ReactNode => {
  return (
    <Row className="pt-8">
      <Col span={12}>
        <label className="option">
          {item.allowanceType === AllowanceType.Annual
            ? translate('admin-dashboard.benefit-details.summary.labels.annual-allowance')
            : translate('admin-dashboard.benefit-details.summary.labels.monthly-allowance')}
          : {formatMoney(item.familyAllowances[0].amount)}
        </label>
      </Col>
    </Row>
  )
}

const differentAllowanceContentByFamilyStatus = (item: ProductClass, translate: (key: string) => string): ReactNode => {
  return (
    <>
      <Row className="pt-8">
        <Col span={12}>
          <label className="option">
            {translate('admin-dashboard.benefit-details.summary.labels.family-status-single')}:{' '}
            {getAllowanceByFamilyStatus(item, FamilyStatus.Single)}
          </label>
        </Col>
        <Col span={12}>
          <label className="option">
            {translate('admin-dashboard.benefit-details.summary.labels.family-status-married')}:{' '}
            {getAllowanceByFamilyStatus(item, FamilyStatus.Married)}
          </label>
        </Col>
      </Row>
      <Row className="pt-8">
        <Col span={12}>
          <label className="option">
            {translate('admin-dashboard.benefit-details.summary.labels.family-status-single-dep')}:{' '}
            {getAllowanceByFamilyStatus(item, FamilyStatus.SingleWithDependents)}
          </label>
        </Col>
        <Col span={12}>
          <label className="option">
            {translate('admin-dashboard.benefit-details.summary.labels.family-status-married-dep')}:{' '}
            {getAllowanceByFamilyStatus(item, FamilyStatus.MarriedWithDependents)}
          </label>
        </Col>
      </Row>
    </>
  )
}

const getEobContent = (item: ProductClass, translate: (key: string) => string): ReactNode => {
  return (
    <Col span={12} className="class_section pl-8 pr-8">
      <div className="title border_bottom">
        <h6>
          {translate('admin-dashboard.benefit-details.summary.class-summary-eob-requirement')}:{' '}
          {getBoolValueOrNotSet(item.requiresEOB, translate)}
        </h6>
      </div>
      {getExemptFromEobValues(item, translate)}
    </Col>
  )
}

const getExemptFromEobValues = (item: ProductClass, translate: (key: string) => string): ReactNode => {
  if (item.requiresEOB && item.eobExpense && item.eobExpense.length > 0) {
    return (
      <div className="content">
        <Row className="pt-8">
          <Col span={12}>
            <label className="option">
              {translate('admin-dashboard.benefit-details.summary.exempt-from-eob')}:{' '}
              {item.eobExpense.map((item: any) => item.name).join(', ')}
              {item.requiresPrescription
                ? `, ${translate('admin-dashboard.benefit-details.summary.prescription-label')}`
                : ''}
            </label>
          </Col>
        </Row>
      </div>
    )
  } else if (item.requiresEOB && (!item.eobExpense || item.eobExpense.length === 0) && item.requiresPrescription) {
    return (
      <div className="content">
        <Row className="pt-8">
          <Col span={12}>
            <label className="option">
              {translate('admin-dashboard.benefit-details.summary.exempt-from-eob')}:{' '}
              {translate('admin-dashboard.benefit-details.summary.prescription-label')}
            </label>
          </Col>
        </Row>
      </div>
    )
  } else {
    return null
  }
}

const getCostSharingAmountContent = (item: ProductClass, translate: (key: string) => string): ReactNode => {
  return (
    <Col span={12} className="class_section pl-8 pr-8">
      <div className="title border_bottom">
        <h6>
          {translate('admin-dashboard.benefit-details.summary.class-hra-deductible')}:{' '}
          {getBoolValueOrNotSet(item.deductible, translate)}
        </h6>
      </div>
      {item.deductible !== null && item.deductible ? (
        <div className="content">
          {item.offerSameAllowance !== null && item.offerSameAllowance
            ? sameDeductiblesContent(item, translate)
            : differentDeductiblesContentByFamilyStatus(item, translate)}
        </div>
      ) : null}
    </Col>
  )
}

const sameDeductiblesContent = (item: ProductClass, translate: (key: string) => string): ReactNode => {
  return (
    <Row className="pt-8">
      <Col span={12}>
        <label className="option">
          {translate('admin-dashboard.benefit-details.summary.labels.deductible-amount')}:{' '}
          {formatMoney(item.familyDeductibles[0].deductible)}
        </label>
      </Col>
    </Row>
  )
}

const sameCostSharingAmountContent = (item: ProductClass, translate: (key: string) => string): ReactNode => {
  return (
    <Row className="pt-8">
      <Col span={12}>
        <label className="option">
          {translate('admin-dashboard.benefit-details.summary.class-cost-sharing-2')}:{' '}
          {item.familyCostSharings[0].costSharing} %
        </label>
      </Col>
    </Row>
  )
}

const differentDeductiblesContentByFamilyStatus = (
  item: ProductClass,
  translate: (key: string) => string,
): ReactNode => {
  return (
    <>
      <Row className="pt-8">
        <Col span={12}>
          <label className="option">
            {translate('admin-dashboard.benefit-details.summary.labels.family-status-single')}:{' '}
            {getDeductiblesAmountByFamilyStatus(item, FamilyStatus.Single)}
          </label>
        </Col>
        <Col span={12}>
          <label className="option">
            {translate('admin-dashboard.benefit-details.summary.labels.family-status-married')}:{' '}
            {getDeductiblesAmountByFamilyStatus(item, FamilyStatus.Married)}
          </label>
        </Col>
      </Row>
      <Row className="pt-8">
        <Col span={12}>
          <label className="option">
            {translate('admin-dashboard.benefit-details.summary.labels.family-status-single-dep')}:{' '}
            {getDeductiblesAmountByFamilyStatus(item, FamilyStatus.SingleWithDependents)}
          </label>
        </Col>
        <Col span={12}>
          <label className="option">
            {translate('admin-dashboard.benefit-details.summary.labels.family-status-married-dep')}:{' '}
            {getDeductiblesAmountByFamilyStatus(item, FamilyStatus.MarriedWithDependents)}
          </label>
        </Col>
      </Row>
    </>
  )
}

const differentClassCostSharingAmountContentByFamilyStatus = (
  item: ProductClass,
  translate: (key: string) => string,
): ReactNode => {
  return (
    <>
      <Row className="pt-8">
        <Col span={12}>
          <label className="option">
            {translate('admin-dashboard.benefit-details.summary.labels.family-status-single')}:{' '}
            {getCostSharingPercentageAmountByFamilyStatus(item, FamilyStatus.Single)} %
          </label>
        </Col>
        <Col span={12}>
          <label className="option">
            {translate('admin-dashboard.benefit-details.summary.labels.family-status-married')}:{' '}
            {getCostSharingPercentageAmountByFamilyStatus(item, FamilyStatus.Married)} %
          </label>
        </Col>
      </Row>
      <Row className="pt-8">
        <Col span={12}>
          <label className="option">
            {translate('admin-dashboard.benefit-details.summary.labels.family-status-single-dep')}:{' '}
            {getCostSharingPercentageAmountByFamilyStatus(item, FamilyStatus.SingleWithDependents)} %
          </label>
        </Col>
        <Col span={12}>
          <label className="option">
            {translate('admin-dashboard.benefit-details.summary.labels.family-status-married-dep')}:{' '}
            {getCostSharingPercentageAmountByFamilyStatus(item, FamilyStatus.MarriedWithDependents)} %
          </label>
        </Col>
      </Row>
    </>
  )
}
const getCostSharingPercentage = (item: ProductClass, translate: (key: string) => string): ReactNode => {
  return (
    <Col span={12} className="class_section pl-8 pr-8">
      <div className="title border_bottom">
        <h6>
          {translate('admin-dashboard.benefit-details.summary.class-cost-sharing')}:{' '}
          {getBoolValueOrNotSet(item.eeCoinsurance, translate)}
        </h6>
      </div>
      {item.eeCoinsurance !== null && item.eeCoinsurance && item.familyCostSharings.length > 0 ? (
        <div className="content">
          {item.offerSameAllowance !== null && item.offerSameAllowance
            ? sameCostSharingAmountContent(item, translate)
            : differentClassCostSharingAmountContentByFamilyStatus(item, translate)}
        </div>
      ) : null}
    </Col>
  )
}

const getBoolValueOrNotSet = (status: boolean | null, translate: (key: string) => string): ReactNode => {
  if (status === null) {
    return translate('admin-dashboard.benefit-details.summary.step-status-not-set')
  }

  return status
    ? translate('admin-dashboard.benefit-details.summary.step-status-yes')
    : translate('admin-dashboard.benefit-details.summary.step-status-no')
}

const getWaitingPeriodContent = (item: ProductClass, translate: (key: string) => string): ReactNode => {
  return (
    <Col span={12} className="class_section pl-8 pr-8">
      <div className="title border_bottom">
        <h6>
          {translate('admin-dashboard.benefit-details.summary.class-waiting-period')}:{' '}
          {item.waitingPeriod === null
            ? translate('admin-dashboard.benefit-details.summary.step-status-not-set')
            : item.waitingPeriod === 0
            ? translate('admin-dashboard.benefit-details.summary.no-waiting-period')
            : `${translate('admin-dashboard.benefit-details.summary.yes-waiting-period')}, 
                            ${item.waitingPeriod}-${translate(
                'admin-dashboard.benefit-details.summary.yes-day-waiting-period',
              )}`}
        </h6>
      </div>
    </Col>
  )
}
const getReimburseAnotherEmployeePlan = (item: ProductClass, translate: (key: string) => string): ReactNode => {
  return (
    <Col span={12} className="class_section pl-8 pr-8">
      <div>
        <h6>
          {translate('csi-dashboard.benefit-classes.reimburse-another-employee-plan')}:{' '}
          {item.reimburseAnotherEmployeePlan
            ? translate('admin-dashboard.benefit-details.qsehra-benefit-design-details.yes')
            : translate('admin-dashboard.benefit-details.qsehra-benefit-design-details.no')}
        </h6>
      </div>
    </Col>
  )
}
const getExpenseType = (item: ProductClass, translate: (key: string) => string): ReactNode => {
  return (
    <Col span={12} className="class_section pl-8 pr-8">
      <div>
        <h6>
          {translate('csi-dashboard.benefit-classes.expense-type')}:{' '}
          <Col span={24}>
            <p className="class-summary__item gray-8 t-expense-type">{item.reimbursementProduct.name}</p>
          </Col>
        </h6>
      </div>
    </Col>
  )
}
const getDateColumns = (translate: (key: string) => string): any[] => {
  const columns = [
    {
      title: translate('csi-dashboard.benefit-classes.employees.start-date'),
      dataIndex: 'startDate',
      key: 'startDate',
      sorter: false,
      render: (text: string, record: any) => {
        return <div>{parseZoneFormat(record.startDate)}</div>
      },
    },
    {
      title: translate('csi-dashboard.benefit-classes.employees.end-date'),
      dataIndex: 'endDate',
      key: 'endDate',
      sorter: false,
      render: (text: string, record: any) => {
        return <div>{record.endDate && parseZoneFormat(record.endDate)}</div>
      },
    },
    {
      title: translate('csi-dashboard.benefit-classes.employees.eligibility-date'),
      dataIndex: 'eligibilityDate',
      key: 'eligibilityDate',
      sorter: false,
      render: (text: string, record: any) => {
        return <div>{parseZoneFormat(record.eligibilityDate)}</div>
      },
    },
    {
      title: translate('csi-dashboard.benefit-classes.employees.created-on'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: false,
      render: (text: string, record: any) => {
        return <div>{parseZoneFormat(record.createdAt)}</div>
      },
    },
    {
      title: translate('csi-dashboard.benefit-classes.employees.created-by'),
      dataIndex: 'createdBy',
      key: 'createdBy',
      sorter: false,
      render: (text: string, record: any) => {
        return <div>{record.createdBy}</div>
      },
    },
  ]

  return columns
}

const getEmployeeColumns = (translate: (key: string) => string): any[] => {
  const columns = [
    {
      title: translate('csi-dashboard.benefit-classes.employees.name/uid/email/status'),
      dataIndex: 'fullName',
      key: 'fullName',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (text: string, record: any) => {
        return (
          <span>
            <div>
              <p>{record.fullName}</p>
              <p>{record.id}</p>
              <p>{record.workEmail}</p>
              <p>Status: {record.status}</p>
            </div>
          </span>
        )
      },
    },
    {
      title: translate('csi-dashboard.benefit-classes.employees.membership'),
      dataIndex: 'eligibilityDates',
      key: 'eligibilityDates',
      sorter: false,
      render: (text: string, record: any) => {
        return <Table dataSource={record.eligibilityDates} columns={getDateColumns(translate)} pagination={false} />
      },
    },
  ]

  return columns
}

const onEmployeesTableChange = (
  pagination: any,
  sorter: any,
  setEmployeesFilter: (values: OrderedPageFilter) => void,
) => {
  setEmployeesFilter({
    pageNumber: pagination.current,
    pageSize: pagination.pageSize,
    orderColumn: sorter.field || 'fullName',
    orderDescending: sorter.order === 'descend',
  })
}

const getEmployees = (
  employees: PagedProductEligibleEmployee,
  translate: (key: string) => string,
  setEmployeesFilter: (values: OrderedPageFilter) => void,
): ReactNode => {
  return (
    <Table
      className="employee-table"
      dataSource={employees.items}
      onChange={(pagination, filter, sorter) => onEmployeesTableChange(pagination, sorter, setEmployeesFilter)}
      columns={getEmployeeColumns(translate)}
      pagination={{
        total: employees.totalItemCount,
        pageSize: employees.pageSize,
        current: employees.pageNumber,
        showSizeChanger: true,
        pageSizeOptions: ['10', '20', '30', '40'],
      }}
    />
  )
}

const Summary = (props: ClassesSummaryProps) => {
  const getClassesPanels = (): ReactNode => {
    return props.classes.map((item, index) => {
      return (
        <Row className="class_summary" key={index}>
          <Col span={24}>
            <ClassSummary
              title={item.name + ' ' + `(${item.totalEmployeesAssigned ?? ''}) `}
              id={item.id}
              loadDataAfterRender={props.sameClassForAll || index === 0}
              apiEndpoint={props.apiEndpoint}
              eligibleEmployeesEndpoint={props.eligibleEmployeesEndpoint}
              item={item}
              getClassSummary={getClassSummary}
              productId={props.productId}
            />
          </Col>
        </Row>
      )
    })
  }

  return <>{getClassesPanels()}</>
}

export default Summary
